import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import ChatMessage from "./chat-message";
import {
    mergeWorkspaceChatMessages,
    removeWorkspaceChatMessage,
    replaceWorkspaceChatMessagesForChat,
    upsertWorkspaceChatMessage,
} from "../../system/workspace-graph-slice";
import {
    normalizeMembershipEntity,
    prepareMembershipPayload,
} from "../workspace-membership/workspace-membership-identity";
import {reconcileRequestedReaction} from "./chat-message-reaction-state";
import {buildChatMessagePageQuery, hasChatMessageFilters} from "./chat-message-query";

export const chatMessagePopulate = {
    user: {populate: {member: {populate: {profileImage: true}}}},
    attachment: true,
    parent: {populate: {
        attachment: true,
        user: {populate: {member: {populate: {profileImage: true}}}},
    }},
    reactions: {
        fields: ["documentId", "emoji", "reactedAt"],
        populate: {
            user: {
                fields: ["documentId"],
                populate: {
                    member: {
                        fields: ["documentId", "firstname", "lastname", "fullname", "username"],
                        populate: {profileImage: true},
                    },
                },
            },
        },
    },
    chat: {fields: ["type", "title"], populate: {project: true}},
};

export function useChatMessage() {
    const dispatch = useDispatch();
    const workspaceMembership = useSelector((state) => state.app?.workspaceMembershipData);
    const memberships = workspaceMembership?.workspace?.workspace_memberships || [];
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const run = async (action) => {
        setLoading(true);
        setError(null);
        try {
            return await action();
        } catch (requestError) {
            setError(requestError);
            throw requestError;
        } finally {
            setLoading(false);
        }
    };

    const getChatMessagePage = async (chatIdentifier, options = {}) => run(async () => {
        const response = await new ChatMessage().getAll(buildChatMessagePageQuery(chatIdentifier, chatMessagePopulate, options));
        const items = (response?.data || []).map(normalizeMessage);
        if (!hasChatMessageFilters(options)) {
            if (options.replace) {
                dispatch(replaceWorkspaceChatMessagesForChat({chat: chatIdentifier, items}));
            } else {
                dispatch(mergeWorkspaceChatMessages(items));
            }
        }
        return {
            items,
            pagination: response?.meta?.pagination || {
                page: Number(options.page || 1),
                pageSize: Number(options.pageSize || 40),
                pageCount: items.length === Number(options.pageSize || 40) ? Number(options.page || 1) + 1 : Number(options.page || 1),
                total: items.length,
            },
        };
    });

    const getChatMessages = async (chatIdentifier) => {
        const result = await getChatMessagePage(chatIdentifier, {page: 1, pageSize: 200});
        return result.items;
    };

    const getChatMessage = async (documentId) => run(async () => {
        try {
            const response = await new ChatMessage().getById(documentId, {populate: chatMessagePopulate});
            return normalizeMessage(response?.data) || null;
        } catch (requestError) {
            if ([403, 404].includes(requestError?.status)) return null;
            throw requestError;
        }
    });

    const createChatMessage = async (data) => run(async () => {
        const payload = prepareMembershipPayload("chatMessage", data, memberships, {
            currentMembership: workspaceMembership,
            operation: "create",
        });
        const response = await new ChatMessage().create(payload, {populate: chatMessagePopulate});
        const item = normalizeMessage(response?.data);
        if (item) dispatch(upsertWorkspaceChatMessage(item));
        return item;
    });

    const updateChatMessage = async (documentId, data) => run(async () => {
        const payload = prepareMembershipPayload("chatMessage", data, memberships, {
            currentMembership: workspaceMembership,
            operation: "update",
        });
        const response = await new ChatMessage().update(documentId, payload, {populate: chatMessagePopulate});
        const item = normalizeMessage(response?.data);
        if (item) dispatch(upsertWorkspaceChatMessage(item));
        return item;
    });

    const deleteChatMessage = async (item) => run(async () => {
        const documentId = item?.documentId ?? item?.id ?? item;
        await new ChatMessage().delete(documentId);
        dispatch(removeWorkspaceChatMessage(typeof item === "object" ? item : {documentId}));
        return true;
    });

    const setChatMessageReaction = async (item, emoji) => {
        const documentId = item?.documentId ?? item?.id ?? item;
        const previous = typeof item === "object" ? item : null;
        const optimistic = previous
            ? reconcileRequestedReaction(previous, workspaceMembership, emoji)
            : null;
        if (optimistic) dispatch(upsertWorkspaceChatMessage(optimistic));

        try {
            return await run(async () => {
                const response = await new ChatMessage().setReaction(documentId, emoji);
                const serverMessage = normalizeMessage(response?.data);
                const updated = reconcileRequestedReaction(
                    previous || serverMessage,
                    workspaceMembership,
                    emoji,
                    serverMessage
                );
                if (updated) dispatch(upsertWorkspaceChatMessage(updated));
                return updated;
            });
        } catch (requestError) {
            if (previous) dispatch(upsertWorkspaceChatMessage(previous));
            throw requestError;
        }
    };

    function normalizeMessage(item) {
        const normalized = normalizeMembershipEntity(item, "chatMessage", memberships);
        if (!normalized) return normalized;
        normalized.parent = normalizeMembershipEntity(normalized.parent, "chatMessage", memberships);
        return normalized;
    }

    return {
        getChatMessages,
        getChatMessagePage,
        getChatMessage,
        createChatMessage,
        updateChatMessage,
        deleteChatMessage,
        setChatMessageReaction,
        loading,
        error,
    };
}
